import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Home,
  Building,
  Brush,
  Sparkles,
  Wrench,
  Palette,
  Users,
  FileText,
  Award,
  MessageSquare,
  Shield,
  Calendar,
  FileCheck,
  CheckCircle,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import Header from '../components/Header';
import { mockProjects, services, whyChooseUs, howItWorks, mockSubmitEstimate } from '../mock';

const iconMap = {
  Home,
  Building,
  Brush,
  Sparkles,
  Wrench,
  Palette,
  MapPin,
  FileText,
  Users,
  Award,
  MessageSquare,
  Shield,
  Calendar,
  FileCheck,
  CheckCircle
};

const LandingPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    serviceType: '',
    propertySize: '',
    preferredDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await mockSubmitEstimate(formData);
      toast.success(response.message);
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        serviceType: '',
        propertySize: '',
        preferredDate: '',
        message: ''
      });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Professional Home Painting Services in{' '}
                <span className="text-blue-900">Los Angeles, CA</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Reliable interior and exterior painting tailored to your home and budget.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  className="bg-blue-900 hover:bg-blue-800 text-white text-lg px-8 py-6 rounded-full transition-all hover:shadow-xl"
                >
                  Request Free Estimate
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="text-blue-900 border-blue-900 hover:bg-blue-50 text-lg px-8 py-6 rounded-full transition-all"
                >
                  <a href="tel:+18717871113">Call Now</a>
                </Button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center text-gray-700">
                  <Check className="w-5 h-5 text-green-600 mr-3" />
                  <span className="font-medium">Experienced Painting Team</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Check className="w-5 h-5 text-green-600 mr-3" />
                  <span className="font-medium">Transparent Written Estimates</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <Check className="w-5 h-5 text-green-600 mr-3" />
                  <span className="font-medium">Clean & Professional Work</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800&q=80"
                alt="Professional painting service"
                className="rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive painting solutions for every part of your home
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-blue-900" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <p className="text-gray-600 bg-blue-50 inline-block px-6 py-3 rounded-lg">
              <strong>Note:</strong> Pricing varies based on home size, surface condition, and project scope.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Simple, transparent process from start to finish
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {howItWorks.map((step) => {
              const Icon = iconMap[step.icon];
              return (
                <div key={step.step} className="text-center">
                  <div className="w-20 h-20 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <Icon className="w-10 h-10" />
                  </div>
                  <div className="bg-blue-900 text-white w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Gallery Section */}
      <section id="projects" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Projects
            </h2>
            <p className="text-xl text-gray-600">
              Quality work delivered for Los Angeles homeowners
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockProjects.map((project) => (
              <Card key={project.id} className="overflow-hidden border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover"
                />
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Livington Designs
            </h2>
            <p className="text-xl text-gray-600">
              Your trusted partner for quality home painting
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={index} className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Icon className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Transparency Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Transparent Pricing
            </h2>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-8">
              <p className="text-lg text-gray-700 mb-4">
                We believe in honest, upfront pricing. Every project is unique, and costs depend on:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-left mb-6">
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-blue-900 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Square footage and room count</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-blue-900 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Surface condition and preparation needs</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-blue-900 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Type of paint and materials selected</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-5 h-5 text-blue-900 mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Project complexity and timeline</span>
                </div>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                You'll receive a detailed written estimate before any work begins – no hidden fees, no surprises.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
              About Livington Designs
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-lg text-gray-700 mb-6">
                  Livington Designs is a locally owned and operated painting company serving Los Angeles and surrounding areas. We specialize in residential painting services with a focus on quality craftsmanship and customer satisfaction.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  Our experienced team brings years of expertise to every project, ensuring professional results that transform your home and exceed your expectations.
                </p>
              </div>
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="w-6 h-6 text-blue-900 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Address</p>
                    <p className="text-gray-700">4512 Sunset Boulevard, Los Angeles, CA 90027</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Phone className="w-6 h-6 text-blue-900 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Phone</p>
                    <a href="tel:+18717871113" className="text-blue-900 hover:underline">
                      +1-871-7871-113
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-blue-900 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Email</p>
                    <a href="mailto:info@livingtone.store" className="text-blue-900 hover:underline">
                      info@livingtone.store
                    </a>
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock className="w-6 h-6 text-blue-900 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Business Hours</p>
                    <p className="text-gray-700">Mon-Fri: 8:00 AM - 6:00 PM</p>
                    <p className="text-gray-700">Sat: 9:00 AM - 4:00 PM</p>
                    <p className="text-gray-700">Sun: Closed</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <MapPin className="w-6 h-6 text-blue-900 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">Service Areas</p>
                    <p className="text-gray-700">Los Angeles and surrounding areas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Get Your Free Estimate
              </h2>
              <p className="text-xl text-gray-600">
                Fill out the form below and we'll get back to you within 24 hours
              </p>
            </div>
            <Card className="border-gray-200 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="mt-2"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                        placeholder="+1-234-567-8900"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="serviceType">Type of Painting Service *</Label>
                    <Select
                      value={formData.serviceType}
                      onValueChange={(value) => handleSelectChange('serviceType', value)}
                      required
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="interior">Interior Painting</SelectItem>
                        <SelectItem value="exterior">Exterior Painting</SelectItem>
                        <SelectItem value="cabinet">Cabinet Painting</SelectItem>
                        <SelectItem value="trim">Trim & Detail Work</SelectItem>
                        <SelectItem value="preparation">Wall Preparation & Repairs</SelectItem>
                        <SelectItem value="consultation">Color Consultation</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="propertySize">Property Size</Label>
                      <Select
                        value={formData.propertySize}
                        onValueChange={(value) => handleSelectChange('propertySize', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select property size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="small">Small (&lt; 1,500 sq ft)</SelectItem>
                          <SelectItem value="medium">Medium (1,500-2,500 sq ft)</SelectItem>
                          <SelectItem value="large">Large (2,500-4,000 sq ft)</SelectItem>
                          <SelectItem value="xlarge">Extra Large (&gt; 4,000 sq ft)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="preferredDate">Preferred Start Date</Label>
                      <Input
                        id="preferredDate"
                        name="preferredDate"
                        type="date"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Additional Details</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="mt-2"
                      rows={4}
                      placeholder="Tell us more about your project..."
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white text-lg py-6 rounded-full transition-all hover:shadow-lg"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get My Free Estimate'}
                  </Button>
                  <p className="text-sm text-gray-600 text-center">
                    We respect your privacy. Your information will only be used to respond to your request.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">Livington Designs</h3>
              <p className="text-gray-400">
                Professional home painting services in Los Angeles, CA
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-gray-400">
                <p>4512 Sunset Boulevard</p>
                <p>Los Angeles, CA 90027</p>
                <p>
                  <a href="tel:+18717871113" className="hover:text-white transition-colors">
                    +1-871-7871-113
                  </a>
                </p>
                <p>
                  <a href="mailto:info@livingtone.store" className="hover:text-white transition-colors">
                    info@livingtone.store
                  </a>
                </p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Hours</h4>
              <div className="space-y-2 text-gray-400">
                <p>Mon-Fri: 8:00 AM - 6:00 PM</p>
                <p>Sat: 9:00 AM - 4:00 PM</p>
                <p>Sun: Closed</p>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Legal</h4>
              <div className="space-y-2">
                <Link to="/privacy-policy" className="block text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/terms-conditions" className="block text-gray-400 hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
                <Link to="/cancellation-policy" className="block text-gray-400 hover:text-white transition-colors">
                  Cancellation Policy
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <p className="text-gray-400 text-sm text-center mb-4">
              <strong>Disclaimer:</strong> Service pricing varies depending on project size, surface condition, and materials selected.
            </p>
            <p className="text-gray-400 text-sm text-center">
              © {new Date().getFullYear()} Livington Designs. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-gray-200 p-4 shadow-lg z-40">
        <div className="flex gap-2">
          <Button
            asChild
            variant="outline"
            className="flex-1 border-blue-900 text-blue-900"
          >
            <a href="tel:+18717871113">
              <Phone className="w-4 h-4 mr-2" />
              Call
            </a>
          </Button>
          <Button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="flex-1 bg-blue-900 hover:bg-blue-800"
          >
            Get Estimate
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
